const runtime = window.__meniconUiRuntime || (window.__meniconUiRuntime = {
    initialized: false,
    activeOverlay: null,
    activeTrigger: null,
    floatingElement: null
});

function selectorFromTrigger(trigger) {
    const selector = trigger.getAttribute('data-target') || trigger.getAttribute('href');

    if (!selector || selector === '#' || selector.charAt(0) !== '#') {
        return null;
    }

    try {
        return document.querySelector(selector);
    } catch (error) {
        return null;
    }
}

function dispatch(element, name, detail) {
    element.dispatchEvent(new CustomEvent(name, {
        bubbles: true,
        detail: detail || {}
    }));
}

function initialiseModal(modal) {
    if (modal.dataset.uiModalReady === 'true') {
        return;
    }

    modal.dataset.uiModalReady = 'true';
    modal.setAttribute('aria-hidden', 'true');
    modal.setAttribute('aria-modal', 'true');
    modal.setAttribute('role', modal.getAttribute('role') || 'dialog');
    modal.hidden = true;
}

export function showModal(target, trigger) {
    const modal = typeof target === 'string' ? document.querySelector(target) : target;
    if (!modal) {
        return false;
    }

    document.querySelectorAll('.modal').forEach(initialiseModal);

    if (runtime.activeOverlay && runtime.activeOverlay !== modal) {
        hideModal(runtime.activeOverlay);
    }

    modal.hidden = false;
    modal.setAttribute('aria-hidden', 'false');
    modal.classList.add('is-open');
    runtime.activeOverlay = modal;
    runtime.activeTrigger = trigger || document.activeElement;
    document.body.classList.add('overflow-hidden');
    dispatch(modal, 'ui:modal:shown');

    const focusable = modal.querySelector(
        '[autofocus], button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
    if (focusable) {
        focusable.focus();
    }

    return true;
}

export function hideModal(target) {
    const modal = typeof target === 'string' ? document.querySelector(target) : target;
    if (!modal) {
        return false;
    }

    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');
    modal.classList.remove('is-open');
    dispatch(modal, 'ui:modal:hidden');

    if (runtime.activeOverlay === modal) {
        runtime.activeOverlay = null;
        document.body.classList.remove('overflow-hidden');

        if (runtime.activeTrigger && typeof runtime.activeTrigger.focus === 'function') {
            runtime.activeTrigger.focus();
        }

        runtime.activeTrigger = null;
    }

    return true;
}

function setExpanded(trigger, expanded) {
    trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
}

function synchroniseCollapseTriggers(panel, expanded) {
    document.querySelectorAll('[data-toggle="collapse"]').forEach((trigger) => {
        if (selectorFromTrigger(trigger) !== panel) {
            return;
        }

        setExpanded(trigger, expanded);
        trigger.classList.toggle('collapsed', !expanded);
    });
}

function toggleCollapse(trigger) {
    const target = selectorFromTrigger(trigger);
    if (!target) {
        return;
    }

    const shouldShow = target.hidden || !target.classList.contains('in');

    if (shouldShow && trigger.dataset.parent) {
        try {
            document.querySelectorAll(trigger.dataset.parent + ' .collapse').forEach((sibling) => {
                if (sibling !== target) {
                    sibling.hidden = true;
                    sibling.classList.remove('in', 'show');
                    synchroniseCollapseTriggers(sibling, false);
                }
            });
        } catch (error) {
            // A malformed legacy selector must not break the rest of the UI.
        }
    }

    target.hidden = !shouldShow;
    target.classList.toggle('in', shouldShow);
    target.classList.toggle('show', shouldShow);
    synchroniseCollapseTriggers(target, shouldShow);
    dispatch(target, shouldShow ? 'ui:collapse:shown' : 'ui:collapse:hidden');
}

function closeDropdowns(except) {
    document.querySelectorAll('[data-toggle="dropdown"]').forEach((trigger) => {
        const parent = trigger.parentElement;
        const menu = selectorFromTrigger(trigger) || (parent ? parent.querySelector('.dropdown-menu') : null);
        if (!menu || menu === except) {
            return;
        }

        menu.hidden = true;
        if (parent) {
            parent.classList.remove('open');
        }
        setExpanded(trigger, false);
    });
}

function toggleDropdown(trigger) {
    const parent = trigger.parentElement;
    const menu = selectorFromTrigger(trigger) || (parent ? parent.querySelector('.dropdown-menu') : null);
    if (!menu) {
        return;
    }

    const shouldShow = menu.hidden;
    closeDropdowns(menu);
    menu.hidden = !shouldShow;
    if (parent) {
        parent.classList.toggle('open', shouldShow);
    }
    setExpanded(trigger, shouldShow);
}

function activateTab(trigger) {
    const panel = selectorFromTrigger(trigger);
    if (!panel) {
        return;
    }

    const tabList = trigger.closest('[role="tablist"], .nav-tabs');
    if (tabList) {
        tabList.querySelectorAll('[data-toggle="tab"]').forEach((tab) => {
            const isActive = tab === trigger;
            tab.classList.toggle('active', isActive);
            const tabItem = tab.closest('li');
            if (tabItem) {
                tabItem.classList.toggle('active', isActive);
            }
            tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
            tab.setAttribute('tabindex', isActive ? '0' : '-1');
        });
    }

    const panelContainer = panel.parentElement;
    if (panelContainer) {
        panelContainer.querySelectorAll('.tab-pane').forEach((candidate) => {
            const isActive = candidate === panel;
            candidate.hidden = !isActive;
            candidate.classList.toggle('active', isActive);
            candidate.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });
    }

    dispatch(trigger, 'ui:tab:shown', { panel });
}

function dismissFloatingElement() {
    if (!runtime.floatingElement) {
        return;
    }

    runtime.floatingElement.remove();
    runtime.floatingElement = null;
}

function createFloatingElement(trigger, type) {
    dismissFloatingElement();

    const source = trigger.getAttribute('data-content') ||
        trigger.getAttribute('data-original-title') ||
        trigger.getAttribute('title');

    if (!source) {
        return;
    }

    const element = document.createElement('div');
    element.className = type === 'tooltip'
        ? 'fixed z-50 max-w-xs rounded bg-gray-900 px-2 py-1 text-xs text-white shadow-lg'
        : 'fixed z-50 max-w-sm rounded-lg bg-white p-3 text-sm text-gray-900 shadow-xl ring-1 ring-black/10';
    element.setAttribute('role', type === 'tooltip' ? 'tooltip' : 'dialog');

    if (type === 'popover' && trigger.getAttribute('data-toggle') === 'popover-html') {
        element.innerHTML = source;
    } else {
        element.textContent = source;
    }

    document.body.appendChild(element);

    const rect = trigger.getBoundingClientRect();
    const top = Math.min(window.innerHeight - element.offsetHeight - 8, rect.bottom + 8);
    const left = Math.min(window.innerWidth - element.offsetWidth - 8, Math.max(8, rect.left));
    element.style.top = String(Math.max(8, top)) + 'px';
    element.style.left = String(left) + 'px';

    runtime.floatingElement = element;
}

function initialiseCarousels() {
    document.querySelectorAll('.carousel').forEach((carousel) => {
        if (carousel.dataset.uiCarouselReady === 'true') {
            return;
        }

        carousel.dataset.uiCarouselReady = 'true';
        const items = Array.from(carousel.querySelectorAll('.carousel-inner > .item'));
        const indicators = Array.from(carousel.querySelectorAll('[data-slide-to]'));
        if (items.length < 2) {
            return;
        }

        let index = Math.max(0, items.findIndex((item) => item.classList.contains('active')));
        const show = (nextIndex) => {
            index = (nextIndex + items.length) % items.length;
            items.forEach((item, itemIndex) => {
                const active = itemIndex === index;
                item.hidden = !active;
                item.classList.toggle('active', active);
            });
            indicators.forEach((indicator, indicatorIndex) => {
                indicator.classList.toggle('active', indicatorIndex === index);
                indicator.setAttribute('aria-current', indicatorIndex === index ? 'true' : 'false');
            });
        };

        indicators.forEach((indicator) => {
            indicator.addEventListener('click', (event) => {
                event.preventDefault();
                show(Number(indicator.getAttribute('data-slide-to')));
            });
        });

        show(index);
        const interval = Number(carousel.getAttribute('data-interval') || 5000);
        if (carousel.getAttribute('data-ride') === 'carousel' && interval > 0) {
            window.setInterval(() => show(index + 1), interval);
        }
    });
}

function synchroniseMaterialField(field) {
    const group = field.closest('.ui-form-group');
    if (!group) {
        return;
    }

    const type = (field.getAttribute('type') || '').toLowerCase();
    if (type === 'checkbox' || type === 'radio' || type === 'hidden') {
        return;
    }

    const value = field.value == null ? '' : String(field.value);
    group.classList.toggle('is-empty', value.length === 0);
}

function initialiseMaterialForms() {
    document.querySelectorAll('.ui-material-form input, .ui-material-form select, .ui-material-form textarea').forEach((field) => {
        synchroniseMaterialField(field);
        const group = field.closest('.ui-form-group');
        if (group) {
            group.classList.toggle('is-focused', document.activeElement === field);
        }
    });
}

function initialiseStaticStates() {
    document.querySelectorAll('.modal').forEach(initialiseModal);

    document.querySelectorAll('.collapse').forEach((element) => {
        if (!element.classList.contains('in') && !element.classList.contains('show')) {
            element.hidden = true;
        }
    });

    document.querySelectorAll('.dropdown-menu').forEach((menu) => {
        if (!menu.closest('.open')) {
            menu.hidden = true;
        }
    });

    document.querySelectorAll('.tab-pane').forEach((panel) => {
        panel.hidden = !panel.classList.contains('active');
    });

    initialiseCarousels();
    initialiseMaterialForms();
}

function closestFromEvent(event, selector) {
    const target = event.target;
    const element = target && target.nodeType === 1 ? target : (target ? target.parentElement : null);

    return element && typeof element.closest === 'function' ? element.closest(selector) : null;
}

function onDocumentClick(event) {
    const trigger = closestFromEvent(event, '[data-toggle]');
    const dismiss = closestFromEvent(event, '[data-dismiss="modal"]');

    if (dismiss) {
        event.preventDefault();
        hideModal(dismiss.closest('.modal'));
        return;
    }

    if (runtime.activeOverlay && event.target === runtime.activeOverlay) {
        if (runtime.activeOverlay.getAttribute('data-backdrop') !== 'static') {
            hideModal(runtime.activeOverlay);
        }
        return;
    }

    if (!trigger) {
        if (runtime.floatingElement && !closestFromEvent(event, '[data-toggle="popover"], [data-toggle="popover-html"]')) {
            dismissFloatingElement();
        }
        closeDropdowns();
        return;
    }

    const type = trigger.getAttribute('data-toggle');
    if (type === 'modal') {
        const modal = selectorFromTrigger(trigger);
        if (modal) {
            event.preventDefault();
            showModal(modal, trigger);
        }
    } else if (type === 'collapse') {
        const panel = selectorFromTrigger(trigger);
        if (panel && panel.contains(event.target)) {
            return;
        }
        event.preventDefault();
        toggleCollapse(trigger);
    } else if (type === 'dropdown') {
        event.preventDefault();
        toggleDropdown(trigger);
    } else if (type === 'tab') {
        event.preventDefault();
        activateTab(trigger);
    } else if (type === 'popover' || type === 'popover-html') {
        event.preventDefault();
        createFloatingElement(trigger, 'popover');
    }
}

function onDocumentKeydown(event) {
    if (event.key === 'Escape') {
        dismissFloatingElement();
        closeDropdowns();
        if (runtime.activeOverlay) {
            hideModal(runtime.activeOverlay);
        }
    }
}

function onDocumentFocus(event) {
    const trigger = closestFromEvent(event, '[data-toggle="tooltip"]');
    if (trigger) {
        createFloatingElement(trigger, 'tooltip');
    }
}

function onDocumentBlur(event) {
    if (closestFromEvent(event, '[data-toggle="tooltip"]')) {
        dismissFloatingElement();
    }
}

export function initUi() {
    if (runtime.initialized) {
        return;
    }

    runtime.initialized = true;
    initialiseStaticStates();
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    document.addEventListener('focusin', onDocumentFocus);
    document.addEventListener('focusout', onDocumentBlur);
    document.addEventListener('focusin', (event) => {
        const field = closestFromEvent(event, '.ui-material-form input, .ui-material-form select, .ui-material-form textarea');
        if (field) {
            const group = field.closest('.ui-form-group');
            if (group) {
                group.classList.add('is-focused');
            }
        }
    });
    document.addEventListener('focusout', (event) => {
        const field = closestFromEvent(event, '.ui-material-form input, .ui-material-form select, .ui-material-form textarea');
        if (field) {
            const group = field.closest('.ui-form-group');
            if (group) {
                group.classList.remove('is-focused');
            }
            synchroniseMaterialField(field);
        }
    });
    document.addEventListener('input', (event) => {
        const field = closestFromEvent(event, '.ui-material-form input, .ui-material-form select, .ui-material-form textarea');
        if (field) {
            synchroniseMaterialField(field);
        }
    });
    document.addEventListener('change', (event) => {
        const field = closestFromEvent(event, '.ui-material-form input, .ui-material-form select, .ui-material-form textarea');
        if (field) {
            synchroniseMaterialField(field);
        }
    });
    document.addEventListener('mouseenter', (event) => {
        const trigger = closestFromEvent(event, '[data-toggle="tooltip"]');
        if (trigger) {
            createFloatingElement(trigger, 'tooltip');
        }
    }, true);
    document.addEventListener('mouseleave', (event) => {
        if (closestFromEvent(event, '[data-toggle="tooltip"]')) {
            dismissFloatingElement();
        }
    }, true);
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUi, { once: true });
} else {
    initUi();
}
